// Chess — rules engine (pure functions).
//
// FRONTEND COPY. Kept in sync BYTE-FOR-BYTE with
// `backend/src/games/chess/rules.js`. The server is the authoritative
// rules source; the client only uses these for legal-move highlights
// + material-score pip math. When updating, update the backend copy
// in the same commit or highlights silently diverge from the server.
//
// Every gameplay decision on the server routes through this module.
// It owns:
//   • FEN parse / stringify (interchange format with the DB snapshot
//     column `chess_sessions.state_json` + the frontend client)
//   • Pseudo-legal move generation per piece type
//   • Legality filter (king-safety after move application)
//   • Castling + en passant + pawn promotion handshake
//   • Terminal detection: checkmate, stalemate, insufficient
//     material, fifty-move rule, threefold repetition
//   • Material point score + "sufficient material to mate" check
//     used by the timeout rule
//
// Position indexing:
//   idx = rank * 8 + file
//   file: 0='a' .. 7='h'
//   rank: 0=rank-1 (white's back rank) .. 7=rank-8 (black's back rank)
//
// Piece codes are two characters: colour letter + piece letter.
//   'wK' 'wQ' 'wR' 'wB' 'wN' 'wP' 'bK' 'bQ' 'bR' 'bB' 'bN' 'bP'
//
// Nothing here talks to the DB, network, or engine state. Pure
// functions keep the test surface tight and let the AI driver in
// `ai.js` reuse the same primitives without any locking.

// ─── Constants ────────────────────────────────────────────────────

// Starting position — rank 1 bottom (white), rank 8 top (black).
// Layout per FEN "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1".
export const STARTING_FEN =
  'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

// Standard material-point values. King has no material value (its
// capture is the game); used only for "sufficient material" check.
export const PIECE_VALUE = {
  P: 1, N: 3, B: 3, R: 5, Q: 9, K: 0,
};

// Promotion options per the rulebook — pawn CANNOT stay, CANNOT
// become a king. UI offers exactly these four.
export const PROMOTION_PIECES = ['q', 'r', 'b', 'n'];

// ─── Square helpers ───────────────────────────────────────────────

export function squareToIdx(sq) {
  const f = sq.charCodeAt(0) - 97;   // 'a' → 0
  const r = sq.charCodeAt(1) - 49;   // '1' → 0
  return r * 8 + f;
}

export function idxToSquare(i) {
  return String.fromCharCode(97 + (i % 8)) + String.fromCharCode(49 + Math.floor(i / 8));
}

function fileOf(i) { return i % 8; }
function rankOf(i) { return Math.floor(i / 8); }
function onBoard(f, r) { return f >= 0 && f < 8 && r >= 0 && r < 8; }
function colourOf(piece) { return piece ? piece[0] : null; }
function typeOf(piece)   { return piece ? piece[1] : null; }
function opposite(c)     { return c === 'w' ? 'b' : 'w'; }

// ─── FEN parse / stringify ────────────────────────────────────────

/**
 * Parse a FEN string into a `Position` object. Throws on malformed
 * input so bad DB snapshots fail loudly at rehydrate time instead of
 * silently producing a half-empty board.
 */
export function parseFen(fen) {
  if (typeof fen !== 'string') throw new Error('FEN must be a string');
  const parts = fen.trim().split(/\s+/);
  if (parts.length < 4) throw new Error('FEN needs at least 4 fields');
  const [layout, active, castling, ep, halfMoveClock = '0', fullMoveNumber = '1'] = parts;

  const board = new Array(64).fill(null);
  const rows = layout.split('/');
  if (rows.length !== 8) throw new Error('FEN board must have 8 ranks');
  // FEN lists rank 8 first, rank 1 last. Our array has rank 0 = rank 1.
  for (let r = 0; r < 8; r++) {
    const row = rows[7 - r];
    let f = 0;
    for (const ch of row) {
      if (/[1-8]/.test(ch)) {
        f += Number(ch);
      } else {
        const colour = ch === ch.toUpperCase() ? 'w' : 'b';
        const type = ch.toUpperCase();
        if (!'KQRBNP'.includes(type)) {
          throw new Error(`FEN bad piece ${ch}`);
        }
        board[r * 8 + f] = colour + type;
        f++;
      }
    }
    if (f !== 8) throw new Error(`FEN rank ${8 - r} doesn't sum to 8`);
  }

  if (active !== 'w' && active !== 'b') {
    throw new Error(`FEN bad active color: ${active}`);
  }

  const rights = { wK: false, wQ: false, bK: false, bQ: false };
  if (castling !== '-') {
    for (const ch of castling) {
      if (ch === 'K') rights.wK = true;
      else if (ch === 'Q') rights.wQ = true;
      else if (ch === 'k') rights.bK = true;
      else if (ch === 'q') rights.bQ = true;
    }
  }

  const enPassant = ep === '-' ? null : squareToIdx(ep);

  return {
    board,
    turn:           active,
    castling:       rights,
    enPassant,
    halfMoveClock:  Number(halfMoveClock) | 0,
    fullMoveNumber: Number(fullMoveNumber) | 0,
  };
}

/** Serialize a `Position` back to a FEN string. */
export function stringifyFen(pos) {
  let layout = '';
  for (let r = 7; r >= 0; r--) {
    let run = 0;
    for (let f = 0; f < 8; f++) {
      const p = pos.board[r * 8 + f];
      if (!p) { run++; continue; }
      if (run) { layout += run; run = 0; }
      layout += colourOf(p) === 'w' ? typeOf(p) : typeOf(p).toLowerCase();
    }
    if (run) layout += run;
    if (r > 0) layout += '/';
  }
  let castling = '';
  if (pos.castling.wK) castling += 'K';
  if (pos.castling.wQ) castling += 'Q';
  if (pos.castling.bK) castling += 'k';
  if (pos.castling.bQ) castling += 'q';
  if (!castling) castling = '-';
  const ep = pos.enPassant == null ? '-' : idxToSquare(pos.enPassant);
  return `${layout} ${pos.turn} ${castling} ${ep} ${pos.halfMoveClock} ${pos.fullMoveNumber}`;
}

/** Deep-clone a position so mutations don't leak into the caller's copy. */
export function clonePosition(pos) {
  return {
    board: pos.board.slice(),
    turn: pos.turn,
    castling: { ...pos.castling },
    enPassant: pos.enPassant,
    halfMoveClock: pos.halfMoveClock,
    fullMoveNumber: pos.fullMoveNumber,
  };
}

// ─── Attack detection ─────────────────────────────────────────────

// Offsets for knights / king / sliders — all in (dFile, dRank) pairs.
const KNIGHT_OFFSETS = [
  [+1, +2], [+2, +1], [+2, -1], [+1, -2],
  [-1, -2], [-2, -1], [-2, +1], [-1, +2],
];
const KING_OFFSETS = [
  [+1, +1], [+1, 0], [+1, -1], [0, -1],
  [-1, -1], [-1, 0], [-1, +1], [0, +1],
];
const BISHOP_DIRS = [[+1, +1], [+1, -1], [-1, -1], [-1, +1]];
const ROOK_DIRS   = [[+1, 0], [0, -1], [-1, 0], [0, +1]];
const QUEEN_DIRS  = BISHOP_DIRS.concat(ROOK_DIRS);

/**
 * Is the given square attacked by any piece of `byColour` in the
 * given position? Used for the king-safety filter AND for castling's
 * "squares passed-through not under attack" check.
 */
export function isSquareAttacked(pos, idx, byColour) {
  const tf = fileOf(idx), tr = rankOf(idx);
  const enemy = byColour;

  // Pawns attack diagonally — for white pawns, attacks are from
  // (tf±1, tr-1); for black pawns, (tf±1, tr+1).
  const pawnDir = enemy === 'w' ? -1 : +1;
  for (const dFile of [-1, +1]) {
    const f = tf + dFile, r = tr + pawnDir;
    if (!onBoard(f, r)) continue;
    const p = pos.board[r * 8 + f];
    if (p === enemy + 'P') return true;
  }

  // Knights.
  for (const [df, dr] of KNIGHT_OFFSETS) {
    const f = tf + df, r = tr + dr;
    if (!onBoard(f, r)) continue;
    const p = pos.board[r * 8 + f];
    if (p === enemy + 'N') return true;
  }

  // King (1-square in any direction — lets us catch king-vs-king
  // adjacency which is itself illegal but needs to block castling).
  for (const [df, dr] of KING_OFFSETS) {
    const f = tf + df, r = tr + dr;
    if (!onBoard(f, r)) continue;
    const p = pos.board[r * 8 + f];
    if (p === enemy + 'K') return true;
  }

  // Bishops / queens on diagonals.
  for (const [df, dr] of BISHOP_DIRS) {
    let f = tf + df, r = tr + dr;
    while (onBoard(f, r)) {
      const p = pos.board[r * 8 + f];
      if (p) {
        if (colourOf(p) === enemy && (typeOf(p) === 'B' || typeOf(p) === 'Q')) return true;
        break;
      }
      f += df; r += dr;
    }
  }

  // Rooks / queens on ranks and files.
  for (const [df, dr] of ROOK_DIRS) {
    let f = tf + df, r = tr + dr;
    while (onBoard(f, r)) {
      const p = pos.board[r * 8 + f];
      if (p) {
        if (colourOf(p) === enemy && (typeOf(p) === 'R' || typeOf(p) === 'Q')) return true;
        break;
      }
      f += df; r += dr;
    }
  }

  return false;
}

/** Find the king of a given colour. Returns `-1` if no such king
 *  exists (only happens in malformed test positions). */
export function findKing(pos, colour) {
  for (let i = 0; i < 64; i++) {
    if (pos.board[i] === colour + 'K') return i;
  }
  return -1;
}

/** Is the side-to-move's king currently in check? */
export function inCheck(pos) {
  const k = findKing(pos, pos.turn);
  if (k < 0) return false;
  return isSquareAttacked(pos, k, opposite(pos.turn));
}

// ─── Pseudo-legal move generation ─────────────────────────────────

/**
 * Shape of a move record returned by the generator:
 *   { from, to, piece, capture?, promotion?, enPassant?, castle? }
 *
 *   from:       source idx
 *   to:         destination idx
 *   piece:      piece code at `from` (e.g. 'wN')
 *   capture?:   piece code captured ('bP') or undefined
 *   promotion?: 'q'|'r'|'b'|'n' — only present when a pawn reaches
 *               the last rank AND the caller has supplied the choice.
 *               The generator emits the move with a sentinel
 *               `needsPromotion: true` when the promotion choice is
 *               unresolved; the UI then opens PromotionModal.
 *   enPassant?: true when this is an en-passant capture
 *   castle?:    'K' | 'Q' for king-side / queen-side castling
 */

function emitPawnMoves(pos, from, out) {
  const colour = colourOf(pos.board[from]);
  const dir    = colour === 'w' ? +1 : -1;
  const startRank    = colour === 'w' ? 1 : 6;       // home rank
  const promotionRank= colour === 'w' ? 7 : 0;       // last rank after move
  const f = fileOf(from), r = rankOf(from);

  // Single square forward.
  let nf = f, nr = r + dir;
  if (onBoard(nf, nr) && !pos.board[nr * 8 + nf]) {
    const to = nr * 8 + nf;
    if (nr === promotionRank) emitPromotions(pos.board[from], from, to, null, out);
    else out.push({ from, to, piece: pos.board[from] });

    // Double square from home rank (only if single was clear).
    if (r === startRank) {
      const nr2 = r + 2 * dir;
      if (!pos.board[nr2 * 8 + nf]) {
        out.push({ from, to: nr2 * 8 + nf, piece: pos.board[from], doublePush: true });
      }
    }
  }

  // Diagonal captures.
  for (const df of [-1, +1]) {
    nf = f + df; nr = r + dir;
    if (!onBoard(nf, nr)) continue;
    const to = nr * 8 + nf;
    const target = pos.board[to];
    if (target && colourOf(target) !== colour) {
      if (nr === promotionRank) emitPromotions(pos.board[from], from, to, target, out);
      else out.push({ from, to, piece: pos.board[from], capture: target });
    }
    // En passant.
    if (!target && pos.enPassant === to) {
      const capIdx = r * 8 + nf;      // pawn being captured sits on mover's rank
      const cap = pos.board[capIdx];
      if (cap && colourOf(cap) !== colour && typeOf(cap) === 'P') {
        out.push({
          from, to, piece: pos.board[from],
          capture: cap, enPassant: true, enPassantCaptureIdx: capIdx,
        });
      }
    }
  }
}

function emitPromotions(piece, from, to, capture, out) {
  for (const promo of PROMOTION_PIECES) {
    out.push({
      from, to, piece,
      capture: capture || undefined,
      promotion: promo,
    });
  }
}

function emitKnightMoves(pos, from, out) {
  const colour = colourOf(pos.board[from]);
  const f = fileOf(from), r = rankOf(from);
  for (const [df, dr] of KNIGHT_OFFSETS) {
    const nf = f + df, nr = r + dr;
    if (!onBoard(nf, nr)) continue;
    const to = nr * 8 + nf;
    const target = pos.board[to];
    if (!target) out.push({ from, to, piece: pos.board[from] });
    else if (colourOf(target) !== colour) out.push({ from, to, piece: pos.board[from], capture: target });
  }
}

function emitSliderMoves(pos, from, dirs, out) {
  const colour = colourOf(pos.board[from]);
  const f = fileOf(from), r = rankOf(from);
  for (const [df, dr] of dirs) {
    let nf = f + df, nr = r + dr;
    while (onBoard(nf, nr)) {
      const to = nr * 8 + nf;
      const target = pos.board[to];
      if (!target) {
        out.push({ from, to, piece: pos.board[from] });
      } else {
        if (colourOf(target) !== colour) out.push({ from, to, piece: pos.board[from], capture: target });
        break;
      }
      nf += df; nr += dr;
    }
  }
}

function emitKingMoves(pos, from, out) {
  const colour = colourOf(pos.board[from]);
  const f = fileOf(from), r = rankOf(from);
  for (const [df, dr] of KING_OFFSETS) {
    const nf = f + df, nr = r + dr;
    if (!onBoard(nf, nr)) continue;
    const to = nr * 8 + nf;
    const target = pos.board[to];
    if (!target) out.push({ from, to, piece: pos.board[from] });
    else if (colourOf(target) !== colour) out.push({ from, to, piece: pos.board[from], capture: target });
  }

  // Castling — all preconditions checked in the legality filter,
  // but we emit the candidate move here so the filter has something
  // to evaluate. Rights-gate is the cheap initial check.
  const rights = pos.castling;
  if (colour === 'w' && from === squareToIdx('e1')) {
    if (rights.wK) out.push({ from, to: squareToIdx('g1'), piece: pos.board[from], castle: 'K' });
    if (rights.wQ) out.push({ from, to: squareToIdx('c1'), piece: pos.board[from], castle: 'Q' });
  } else if (colour === 'b' && from === squareToIdx('e8')) {
    if (rights.bK) out.push({ from, to: squareToIdx('g8'), piece: pos.board[from], castle: 'K' });
    if (rights.bQ) out.push({ from, to: squareToIdx('c8'), piece: pos.board[from], castle: 'Q' });
  }
}

/** All pseudo-legal moves for the side to move. Doesn't filter king
 *  safety — call `legalMoves()` for that. */
export function pseudoLegalMoves(pos) {
  const out = [];
  for (let i = 0; i < 64; i++) {
    const p = pos.board[i];
    if (!p || colourOf(p) !== pos.turn) continue;
    const t = typeOf(p);
    if (t === 'P')      emitPawnMoves   (pos, i, out);
    else if (t === 'N') emitKnightMoves (pos, i, out);
    else if (t === 'B') emitSliderMoves (pos, i, BISHOP_DIRS, out);
    else if (t === 'R') emitSliderMoves (pos, i, ROOK_DIRS,   out);
    else if (t === 'Q') emitSliderMoves (pos, i, QUEEN_DIRS,  out);
    else if (t === 'K') emitKingMoves   (pos, i, out);
  }
  return out;
}

// ─── Applying a move ──────────────────────────────────────────────

/**
 * Return a new position with `move` applied. Doesn't mutate input.
 * Updates castling rights, en passant target, halfmove clock, and
 * full move number per FEN rules. If the move is a castle, moves
 * the rook too. If it's a promotion, replaces the pawn.
 *
 * Caller is expected to have validated the move via `legalMoves()`
 * (the engine wrapper does this). This function performs no extra
 * validation beyond what's needed for bookkeeping.
 */
export function applyMove(pos, move) {
  const next = clonePosition(pos);
  const mover  = next.board[move.from];
  const colour = colourOf(mover);
  const isPawn = typeOf(mover) === 'P';
  const capture = move.capture || next.board[move.to];

  // Halfmove clock resets on pawn moves + captures, increments otherwise.
  if (isPawn || capture) next.halfMoveClock = 0;
  else next.halfMoveClock++;

  // Remove captured piece (en passant captures a piece off the `to` square).
  if (move.enPassant) {
    next.board[move.enPassantCaptureIdx] = null;
  }

  // Move the piece.
  next.board[move.to] = mover;
  next.board[move.from] = null;

  // Promotion — swap the pawn for the chosen piece.
  if (move.promotion) {
    next.board[move.to] = colour + move.promotion.toUpperCase();
  }

  // Castling — move the rook too.
  if (move.castle === 'K') {
    const homeRank = colour === 'w' ? 0 : 7;
    const rookFrom = homeRank * 8 + 7;
    const rookTo   = homeRank * 8 + 5;
    next.board[rookTo]   = next.board[rookFrom];
    next.board[rookFrom] = null;
  } else if (move.castle === 'Q') {
    const homeRank = colour === 'w' ? 0 : 7;
    const rookFrom = homeRank * 8 + 0;
    const rookTo   = homeRank * 8 + 3;
    next.board[rookTo]   = next.board[rookFrom];
    next.board[rookFrom] = null;
  }

  // Castling rights — once lost, never restored.
  if (typeOf(mover) === 'K') {
    if (colour === 'w') { next.castling.wK = false; next.castling.wQ = false; }
    else                { next.castling.bK = false; next.castling.bQ = false; }
  }
  if (typeOf(mover) === 'R') {
    if (colour === 'w') {
      if (move.from === squareToIdx('a1')) next.castling.wQ = false;
      if (move.from === squareToIdx('h1')) next.castling.wK = false;
    } else {
      if (move.from === squareToIdx('a8')) next.castling.bQ = false;
      if (move.from === squareToIdx('h8')) next.castling.bK = false;
    }
  }
  // Captured rook on its original square also drops the rights.
  if (move.to === squareToIdx('a1')) next.castling.wQ = false;
  if (move.to === squareToIdx('h1')) next.castling.wK = false;
  if (move.to === squareToIdx('a8')) next.castling.bQ = false;
  if (move.to === squareToIdx('h8')) next.castling.bK = false;

  // En passant target: set only on a pawn double-push.
  if (isPawn && move.doublePush) {
    const midRank = (rankOf(move.from) + rankOf(move.to)) / 2;
    next.enPassant = midRank * 8 + fileOf(move.from);
  } else {
    next.enPassant = null;
  }

  // Turn + full-move number.
  if (colour === 'b') next.fullMoveNumber++;
  next.turn = opposite(colour);

  return next;
}

// ─── Legality filter ──────────────────────────────────────────────

/**
 * Strictly legal moves for the side to move. Castling preconditions
 * (king not in check, passes-through not attacked, squares empty)
 * are enforced here.
 */
export function legalMoves(pos) {
  const pseudo = pseudoLegalMoves(pos);
  const result = [];
  const enemy = opposite(pos.turn);
  for (const m of pseudo) {
    // Castling — in addition to king-safety-after-move we must
    // verify every square the king passes through is safe AND all
    // squares between king and rook are empty.
    if (m.castle) {
      // Currently in check? Castling illegal.
      if (inCheck(pos)) continue;
      const homeRank = pos.turn === 'w' ? 0 : 7;
      if (m.castle === 'K') {
        // f-file + g-file must be empty; king crosses f, lands on g.
        if (pos.board[homeRank * 8 + 5]) continue;
        if (pos.board[homeRank * 8 + 6]) continue;
        if (isSquareAttacked(pos, homeRank * 8 + 5, enemy)) continue;
        if (isSquareAttacked(pos, homeRank * 8 + 6, enemy)) continue;
      } else {
        // b-file + c-file + d-file must be empty; king crosses d, lands on c.
        if (pos.board[homeRank * 8 + 1]) continue;
        if (pos.board[homeRank * 8 + 2]) continue;
        if (pos.board[homeRank * 8 + 3]) continue;
        // King crosses d-file and lands on c-file — both must be safe.
        // b-file isn't crossed by the king (only the rook) so it may be attacked.
        if (isSquareAttacked(pos, homeRank * 8 + 3, enemy)) continue;
        if (isSquareAttacked(pos, homeRank * 8 + 2, enemy)) continue;
      }
    }
    // Simulate + check king safety.
    const after = applyMove(pos, m);
    const kSq = findKing(after, pos.turn);
    if (kSq < 0) continue;
    if (isSquareAttacked(after, kSq, enemy)) continue;
    result.push(m);
  }
  return result;
}

/** Only the legal moves FROM a single square. Used by the UI for
 *  the "click-own-piece → highlight legal targets" interaction. */
export function legalMovesFrom(pos, sqIdx) {
  return legalMoves(pos).filter((m) => m.from === sqIdx);
}

// ─── Terminal detection ───────────────────────────────────────────

/** Sum of material points for one colour (king excluded — its
 *  capture ends the game, not the scoring). */
export function materialPoints(pos, colour) {
  let score = 0;
  for (const p of pos.board) {
    if (p && colourOf(p) === colour) score += PIECE_VALUE[typeOf(p)] || 0;
  }
  return score;
}

/** Material difference from `colour`'s perspective (+N = `colour` up). */
export function materialDiff(pos, colour) {
  return materialPoints(pos, colour) - materialPoints(pos, opposite(colour));
}

/**
 * Rulebook "Draw by Insufficient Material": neither side can force
 * checkmate. Four canonical patterns:
 *   K vs K
 *   K + B vs K
 *   K + N vs K
 *   K + B vs K + B (same-coloured bishops)
 */
export function hasInsufficientMaterial(pos) {
  const pieces = { w: [], b: [] };
  for (let i = 0; i < 64; i++) {
    const p = pos.board[i];
    if (!p) continue;
    pieces[colourOf(p)].push({ type: typeOf(p), idx: i });
  }
  for (const c of ['w', 'b']) {
    // Any pawn / rook / queen on either side → material sufficient.
    if (pieces[c].some((x) => 'PRQ'.includes(x.type))) return false;
  }
  // At this point both sides are kings + bishops/knights only.
  const wMinors = pieces.w.filter((x) => x.type !== 'K');
  const bMinors = pieces.b.filter((x) => x.type !== 'K');
  if (wMinors.length === 0 && bMinors.length === 0) return true;      // K vs K
  if (wMinors.length + bMinors.length === 1) return true;             // K+(B|N) vs K
  if (wMinors.length === 1 && bMinors.length === 1
      && wMinors[0].type === 'B' && bMinors[0].type === 'B') {
    // Same-colour bishops → perpetual draw on opposite-colour square-bishop
    // patterns isn't forced mate. Check both bishops sit on same colour square.
    const sqColour = (i) => (fileOf(i) + rankOf(i)) % 2;
    if (sqColour(wMinors[0].idx) === sqColour(bMinors[0].idx)) return true;
  }
  return false;
}

/**
 * Which colour (if any) has "sufficient material to theoretically
 * checkmate the opponent"? Used when the opponent flags on time —
 * rulebook: you only win by timeout if you could still mate.
 *
 *   K alone               → cannot mate
 *   K + N or K + B alone  → cannot mate
 *   Everything else       → can mate
 */
export function canColourMate(pos, colour) {
  const mine = [];
  for (let i = 0; i < 64; i++) {
    const p = pos.board[i];
    if (p && colourOf(p) === colour) mine.push(typeOf(p));
  }
  if (mine.some((t) => 'PQR'.includes(t))) return true;
  const minors = mine.filter((t) => t === 'B' || t === 'N').length;
  if (minors >= 2) return true;
  return false;
}

/**
 * Terminal-state detector. Call AFTER applying the candidate move.
 * Returns one of:
 *   { reason: 'checkmate', winner: 'w'|'b' }
 *   { reason: 'stalemate' }
 *   { reason: 'insufficient_material' }
 *   { reason: 'fifty_move' }     — claimable (halfmove ≥ 100)
 *   null                          — game continues
 *
 * Threefold repetition isn't detected here — it depends on the full
 * move history, which the engine tracks. `isThreefold(history, pos)`
 * covers that case.
 */
export function detectTerminal(pos) {
  const moves = legalMoves(pos);
  if (moves.length === 0) {
    if (inCheck(pos)) {
      return { reason: 'checkmate', winner: opposite(pos.turn) };
    }
    return { reason: 'stalemate' };
  }
  if (hasInsufficientMaterial(pos)) return { reason: 'insufficient_material' };
  if (pos.halfMoveClock >= 100) return { reason: 'fifty_move' };   // claimable
  return null;
}

/**
 * Threefold repetition check. The engine stores a list of position
 * "hashes" (minus the halfmove clock + fullmove number since those
 * don't affect legal moves) and this just counts occurrences.
 *
 *   positionHash(pos) — stable key used for repetition counting.
 */
export function positionHash(pos) {
  // Board + turn + castling rights + en-passant target. Halfmove /
  // fullmove numbers are deliberately excluded: two identical board
  // states with different move counters are still "the same position"
  // for threefold purposes.
  let h = pos.turn + '|';
  for (const p of pos.board) h += p || '.';
  h += '|' + (pos.castling.wK ? 'K' : '') + (pos.castling.wQ ? 'Q' : '')
          + (pos.castling.bK ? 'k' : '') + (pos.castling.bQ ? 'q' : '') + '|';
  h += pos.enPassant == null ? '-' : pos.enPassant;
  return h;
}

export function isThreefold(positionHashes) {
  // positionHashes: array of `positionHash(pos)` per half-move
  // (including the current position). Returns true if any hash
  // appears ≥ 3 times.
  const counts = new Map();
  for (const h of positionHashes) counts.set(h, (counts.get(h) || 0) + 1);
  for (const n of counts.values()) if (n >= 3) return true;
  return false;
}

// ─── Move → SAN (compact notation) ────────────────────────────────

/**
 * Standard Algebraic Notation generator. Used for the move-history
 * panel + the `san` field in `chess_move_made` broadcasts.
 *
 * Minimal implementation — covers all mandatory disambiguations
 * (file, rank, or full square), captures, promotions, castles,
 * check (+), and checkmate (#).
 */
export function moveToSan(pos, move) {
  if (move.castle === 'K') return sanCheckSuffix(pos, move, 'O-O');
  if (move.castle === 'Q') return sanCheckSuffix(pos, move, 'O-O-O');

  const mover = pos.board[move.from];
  const type = typeOf(mover);
  const isCapture = Boolean(move.capture);
  const toSq = idxToSquare(move.to);
  let san = '';

  if (type === 'P') {
    if (isCapture) san += idxToSquare(move.from)[0] + 'x';
    san += toSq;
    if (move.promotion) san += '=' + move.promotion.toUpperCase();
  } else {
    san += type;
    // Disambiguation — are there other same-type pieces that could
    // move to `to`? If yes, add file / rank / full square to
    // distinguish. Standard algorithm.
    const sameAttackers = legalMoves(pos).filter((m) =>
      m !== move &&
      m.to === move.to &&
      m.from !== move.from &&
      pos.board[m.from] === mover);
    if (sameAttackers.length > 0) {
      const sameFile = sameAttackers.some((m) => fileOf(m.from) === fileOf(move.from));
      const sameRank = sameAttackers.some((m) => rankOf(m.from) === rankOf(move.from));
      const fromSq = idxToSquare(move.from);
      if (!sameFile) san += fromSq[0];
      else if (!sameRank) san += fromSq[1];
      else san += fromSq;
    }
    if (isCapture) san += 'x';
    san += toSq;
  }
  return sanCheckSuffix(pos, move, san);
}

function sanCheckSuffix(pos, move, san) {
  const after = applyMove(pos, move);
  if (legalMoves(after).length === 0 && inCheck(after)) return san + '#';
  if (inCheck(after)) return san + '+';
  return san;
}
